/**
 * GetStarted.js
 * ---------------------------------------------------------------
 * Phase K.5 — Get Started welcome experience for new nursery owners.
 *
 * Exports:
 *   - WelcomeHeroCard         (big dismissible card at top of admin dashboard)
 *   - GettingStartedChecklist (full 8-step checklist, standalone tab-ready)
 *   - GetStartedSidebarPill   (compact progress pill for sidebar when dismissed)
 *   - useOnboardingChecklist  (hook: {data, refresh, loading})
 *   - StuckHelpCTA            (48h+ escape hatch)
 *
 * Design notes:
 *   - Zero external deps for confetti — a tiny inline SVG burst + CSS.
 *   - Uses the plain `card / btn / stat-card` classes so it inherits the
 *     admin theme perfectly.
 *   - Every interactive element gets a data-testid.
 *   - Deep-links use React Router `useNavigate` when available, falling
 *     back to `window.location` so this component works even outside
 *     the routed subtree (e.g. old dashboards that don't wrap it).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';

// ---------------------------------------------------------------
// API wrappers (kept local so this file is self-contained)
// ---------------------------------------------------------------
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

async function fetchChecklist() {
  const res = await fetch(`${API_URL}/api/onboarding/checklist`, { headers: authHeader() });
  if (!res.ok) throw new Error('Failed to load checklist');
  return res.json();
}

async function dismissWelcome() {
  const res = await fetch(`${API_URL}/api/onboarding/dismiss-welcome`, {
    method: 'POST', headers: authHeader(),
  });
  if (!res.ok) {
    // Subscription-locked orgs can't dismiss — swallow gracefully.
    return { ok: false, error: 'locked' };
  }
  return res.json();
}

async function reopenWelcome() {
  const res = await fetch(`${API_URL}/api/onboarding/reopen-welcome`, {
    method: 'POST', headers: authHeader(),
  });
  if (!res.ok) return { ok: false, error: 'locked' };
  return res.json();
}

async function submitStuckHelp(payload) {
  const res = await fetch(`${API_URL}/api/public/demo-request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Failed to submit help request');
  return res.json();
}

// ---------------------------------------------------------------
// Hook: useOnboardingChecklist — shared state for hero + full view
// ---------------------------------------------------------------
export function useOnboardingChecklist() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchChecklist();
      setData(r);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Poll every 60s so completion appears without a hard reload after
    // the user finishes a step in another tab.
    const iv = setInterval(refresh, 60_000);
    return () => clearInterval(iv);
  }, [refresh]);

  return { data, loading, error, refresh };
}

// ---------------------------------------------------------------
// Nav helper — respect user's React Router if it's present.
// ---------------------------------------------------------------
function goToPath(path, onNavigate) {
  if (onNavigate) return onNavigate(path);
  window.location.assign(path);
}

// ---------------------------------------------------------------
// <WelcomeHeroCard /> — big top-of-dashboard banner for new owners
// ---------------------------------------------------------------
export function WelcomeHeroCard({ user, onNavigate }) {
  const { data, loading, refresh } = useOnboardingChecklist();
  const [dismissing, setDismissing] = useState(false);
  const [celebrated, setCelebrated] = useState(false);

  // Auto-celebrate the first time we render at 100%.
  useEffect(() => {
    if (data?.all_done && !celebrated) {
      setCelebrated(true);
    }
  }, [data, celebrated]);

  if (loading && !data) return null;
  if (!data) return null;
  if (data.welcome_dismissed && !data.all_done) return null;

  const firstName = ((user?.full_name || '').split(' ')[0] || 'there');
  const remaining = (data.steps || []).filter((s) => !s.done);
  const nextStep = remaining[0];

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await dismissWelcome();
      await refresh();
    } finally {
      setDismissing(false);
    }
  };

  // 🎉 100% celebration view
  if (data.all_done) {
    return (
      <div
        className="card"
        data-testid="welcome-hero-celebrate"
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, hsl(var(--primary) / 0.10), hsl(var(--primary) / 0.02))',
          border: '1px solid hsl(var(--primary) / 0.30)',
          marginBottom: '1.25rem',
        }}
      >
        <ConfettiBurst show={celebrated} />
        <div style={{ position: 'relative', zIndex: 2 }}>
          <h2 style={{ margin: '0 0 0.35rem 0', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            🎉 You're all set, {firstName}!
          </h2>
          <p style={{ margin: '0 0 1rem 0', color: 'hsl(var(--muted-foreground))' }}>
            All 8 setup steps complete for <strong>{data.organization_name}</strong>. Here's what most nurseries do next:
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={() => goToPath('/admin?tab=invoices&action=create', onNavigate)} data-testid="celebrate-first-invoice">
              📄 Send my first invoice
            </button>
            <button className="btn btn-ghost" onClick={() => goToPath('/admin?tab=moments&action=new', onNavigate)} data-testid="celebrate-first-moment">
              📸 Post my first Moment
            </button>
            <button className="btn btn-ghost" onClick={() => goToPath('/admin?tab=bookings&action=new', onNavigate)} data-testid="celebrate-first-booking">
              📅 Book a trial visit
            </button>
            <button className="btn btn-ghost" onClick={handleDismiss} disabled={dismissing} data-testid="celebrate-dismiss">
              ✕ Hide this
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Standard in-progress hero
  return (
    <div
      className="card"
      data-testid="welcome-hero-card"
      style={{
        marginBottom: '1.25rem',
        background: 'linear-gradient(135deg, hsl(var(--primary) / 0.06), hsl(var(--background)))',
        border: '1px solid hsl(var(--primary) / 0.25)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h2 style={{ margin: '0 0 0.35rem 0' }}>
            👋 Welcome to Kids WiseApp, {firstName}!
          </h2>
          <p style={{ margin: 0, color: 'hsl(var(--muted-foreground))', fontSize: '0.95rem' }}>
            You're <strong data-testid="hero-progress-pct">{data.progress_pct}%</strong> set up — about <strong>{data.total - data.done_count} × 3 min</strong> of tasks left.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={handleDismiss}
          disabled={dismissing}
          data-testid="hero-dismiss-btn"
          style={{ fontSize: '0.85rem' }}
        >
          Hide for now
        </button>
      </div>

      <ProgressBar value={data.progress_pct} />

      {nextStep && (
        <div
          style={{
            marginTop: '1rem',
            display: 'flex',
            gap: '0.75rem',
            alignItems: 'center',
            padding: '0.75rem 1rem',
            background: 'hsl(var(--background))',
            borderRadius: 'var(--radius)',
            border: '1px solid hsl(var(--border))',
          }}
          data-testid="hero-next-step"
        >
          <div style={{ fontSize: '1.6rem' }}>👉</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: 'hsl(var(--muted-foreground))', fontWeight: 700, letterSpacing: '0.05em' }}>
              Next up · {nextStep.est}
            </div>
            <div style={{ fontWeight: 600, marginTop: 2 }}>{nextStep.title}</div>
            <div style={{ fontSize: '0.82rem', color: 'hsl(var(--muted-foreground))' }}>{nextStep.why}</div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => goToPath(nextStep.path, onNavigate)}
            data-testid={`hero-cta-${nextStep.key}`}
          >
            Start →
          </button>
        </div>
      )}

      <div style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
        <a
          href="#get-started"
          onClick={(e) => { e.preventDefault(); goToPath('/admin?tab=get-started', onNavigate); }}
          data-testid="hero-see-all-link"
          style={{ color: 'hsl(var(--primary))', textDecoration: 'none', fontWeight: 600 }}
        >
          See all {data.total} steps →
        </a>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// <GettingStartedChecklist /> — full 8-step tab view
// ---------------------------------------------------------------
export function GettingStartedChecklist({ user, onNavigate }) {
  const { data, loading, refresh } = useOnboardingChecklist();
  const [showStuck, setShowStuck] = useState(false);

  if (loading && !data) return <div>Loading your checklist…</div>;
  if (!data) return <div className="empty-state">Failed to load the checklist</div>;

  const stuckThresholdMs = 48 * 3600 * 1000;
  const createdAt = data.org_created_at ? new Date(data.org_created_at).getTime() : null;
  const isStuck = createdAt && (Date.now() - createdAt) > stuckThresholdMs && data.progress_pct < 50;

  return (
    <div data-testid="get-started-tab-root">
      <div className="page-header">
        <h1>🚀 Get Started</h1>
        <p>Your setup roadmap for {data.organization_name}. Tap any step to jump right in.</p>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }} data-testid="checklist-progress-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
          <div style={{ fontSize: '0.9rem', color: 'hsl(var(--muted-foreground))' }}>
            <strong style={{ color: 'hsl(var(--foreground))', fontSize: '1.15rem' }}>{data.done_count}</strong> / {data.total} steps complete
          </div>
          <div style={{ fontWeight: 700, color: 'hsl(var(--primary))' }} data-testid="checklist-progress-pct">
            {data.progress_pct}%
          </div>
        </div>
        <ProgressBar value={data.progress_pct} />
        {data.welcome_dismissed && (
          <button
            className="btn btn-ghost"
            style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}
            onClick={async () => { await reopenWelcome(); await refresh(); }}
            data-testid="checklist-reopen-welcome"
          >
            Show welcome banner again
          </button>
        )}
      </div>

      {isStuck && (
        <StuckHelpCTA
          user={user}
          orgId={data.organization_id}
          onDismiss={() => setShowStuck(false)}
          open={showStuck}
          onOpen={() => setShowStuck(true)}
        />
      )}

      <div style={{ display: 'grid', gap: '0.75rem' }} data-testid="checklist-steps">
        {data.steps.map((step, idx) => (
          <ChecklistRow
            key={step.key}
            step={step}
            idx={idx}
            onGo={() => goToPath(step.path, onNavigate)}
          />
        ))}
      </div>

      {data.all_done && (
        <div className="card" style={{ marginTop: '1.5rem', background: 'linear-gradient(135deg, hsl(var(--primary) / 0.10), hsl(var(--primary) / 0.02))' }} data-testid="checklist-all-done">
          <h3 style={{ margin: '0 0 0.5rem 0' }}>🎉 All done — you're ready for parents!</h3>
          <p style={{ margin: 0, color: 'hsl(var(--muted-foreground))' }}>
            Send your first invoice, post a Moment, or book a trial visit to see the full flow.
          </p>
        </div>
      )}
    </div>
  );
}

function ChecklistRow({ step, idx, onGo }) {
  return (
    <div
      className="card"
      data-testid={`checklist-row-${step.key}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '0.85rem 1rem',
        borderColor: step.done ? 'hsl(140 60% 40% / 0.4)' : 'hsl(var(--border))',
        background: step.done ? 'hsl(140 60% 45% / 0.06)' : 'hsl(var(--background))',
      }}
    >
      <div
        style={{
          width: 32, height: 32, borderRadius: '50%',
          background: step.done ? 'hsl(140 60% 40%)' : 'hsl(var(--muted))',
          color: step.done ? 'white' : 'hsl(var(--muted-foreground))',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, flexShrink: 0,
        }}
        aria-label={step.done ? 'Done' : `Step ${idx + 1}`}
      >
        {step.done ? '✓' : idx + 1}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 600, textDecoration: step.done ? 'line-through' : 'none', opacity: step.done ? 0.7 : 1 }}>
            {step.title}
          </div>
          <div style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {step.est}
          </div>
        </div>
        <div style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', marginTop: 2 }}>
          {step.why}
        </div>
      </div>
      <button
        className={step.done ? 'btn btn-ghost' : 'btn btn-primary'}
        onClick={onGo}
        data-testid={`checklist-cta-${step.key}`}
      >
        {step.done ? 'Review' : 'Start'} →
      </button>
    </div>
  );
}

// ---------------------------------------------------------------
// <GetStartedSidebarPill /> — compact reminder in the sidebar
// ---------------------------------------------------------------
export function GetStartedSidebarPill({ onNavigate }) {
  const { data } = useOnboardingChecklist();
  if (!data || data.all_done) return null;
  return (
    <button
      type="button"
      className="nav-item"
      onClick={() => goToPath('/admin?tab=get-started', onNavigate)}
      data-testid="sidebar-get-started-pill"
      style={{
        background: 'hsl(var(--primary) / 0.10)',
        border: '1px dashed hsl(var(--primary) / 0.35)',
        borderRadius: 'var(--radius)',
        padding: '0.5rem 0.8rem',
        margin: '0.35rem 0.5rem',
        fontSize: '0.8rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
      }}
    >
      <span>🚀 Get Started</span>
      <span style={{ fontWeight: 700, color: 'hsl(var(--primary))' }}>{data.progress_pct}%</span>
    </button>
  );
}

// ---------------------------------------------------------------
// <StuckHelpCTA /> — 48h+ rescue CTA
// ---------------------------------------------------------------
export function StuckHelpCTA({ user, orgId, open, onOpen, onDismiss }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const submit = async () => {
    setBusy(true); setError('');
    try {
      await submitStuckHelp({
        name: user?.full_name || 'Unknown',
        email: user?.email || '',
        phone: user?.phone || '',
        nursery: '',
        message: msg || 'Please help me finish setting up my nursery.',
        source: 'onboarding_stuck_cta',
        onboarding_help: true,
        stuck_org_id: orgId,
      });
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="card" style={{ marginBottom: '1rem', background: 'hsl(140 60% 45% / 0.08)', border: '1px solid hsl(140 60% 45% / 0.35)' }} data-testid="stuck-cta-sent">
        <h3 style={{ margin: '0 0 0.35rem 0' }}>✅ Got it — we'll be in touch</h3>
        <p style={{ margin: 0, color: 'hsl(var(--muted-foreground))', fontSize: '0.9rem' }}>
          A real person will reach out within one working day to book your free 15-minute onboarding call.
        </p>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: '1rem', background: 'hsl(35 90% 50% / 0.06)', border: '1px dashed hsl(35 90% 50% / 0.35)' }} data-testid="stuck-cta">
      {!open ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div>
            <div style={{ fontWeight: 700 }}>🤝 Need a hand finishing setup?</div>
            <div style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
              Book a free 15-minute onboarding call — no pressure, no sales pitch.
            </div>
          </div>
          <button className="btn btn-primary" onClick={onOpen} data-testid="stuck-cta-open">📞 Get help</button>
        </div>
      ) : (
        <div>
          <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Tell us where you're stuck</div>
          <textarea
            placeholder="e.g. Not sure how to import my existing children..."
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            rows={3}
            data-testid="stuck-cta-textarea"
            style={{ width: '100%', padding: '0.5rem', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)', fontSize: '0.9rem' }}
          />
          {error && <div style={{ color: 'hsl(0 65% 50%)', fontSize: '0.85rem', marginTop: '0.4rem' }}>{error}</div>}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button className="btn btn-primary" onClick={submit} disabled={busy} data-testid="stuck-cta-submit">
              {busy ? 'Sending…' : 'Send request'}
            </button>
            <button className="btn btn-ghost" onClick={onDismiss} disabled={busy} data-testid="stuck-cta-cancel">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// Presentational bits
// ---------------------------------------------------------------
function ProgressBar({ value }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div style={{ height: 8, background: 'hsl(var(--muted))', borderRadius: 999, overflow: 'hidden' }} data-testid="progress-bar">
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          background: 'linear-gradient(90deg, hsl(var(--primary)), hsl(var(--primary) / 0.7))',
          borderRadius: 999,
          transition: 'width 400ms ease-out',
        }}
      />
    </div>
  );
}

function ConfettiBurst({ show }) {
  // Cheap CSS-only celebratory particles — 12 dots spring outward.
  if (!show) return null;
  const dots = Array.from({ length: 14 });
  return (
    <div aria-hidden style={{
      position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden',
    }}>
      {dots.map((_, i) => {
        const angle = (i / dots.length) * Math.PI * 2;
        const dx = Math.cos(angle) * 140;
        const dy = Math.sin(angle) * 90;
        const colors = ['#22c55e', '#3b82f6', '#eab308', '#ef4444', '#a855f7', '#14b8a6'];
        return (
          <span
            key={i}
            style={{
              position: 'absolute', top: '30%', left: '25%',
              width: 8, height: 8,
              background: colors[i % colors.length],
              borderRadius: 2,
              animation: `confetti-burst-${i} 1.2s ease-out forwards`,
              transform: 'translate(-50%, -50%)',
              opacity: 0,
            }}
          />
        );
      })}
      <style>{`
        ${dots.map((_, i) => {
          const angle = (i / dots.length) * Math.PI * 2;
          const dx = Math.cos(angle) * 140;
          const dy = Math.sin(angle) * 90;
          return `
            @keyframes confetti-burst-${i} {
              0%   { transform: translate(-50%, -50%) scale(0.4); opacity: 0; }
              10%  { opacity: 1; }
              100% { transform: translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(1); opacity: 0; }
            }
          `;
        }).join('\n')}
      `}</style>
    </div>
  );
}
